import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import { buildStripeApplePayRequest } from '@/utils/applePay';
import { buildAdyenApplePayPaymentRequest } from '@/utils/adyen';
import { Errors } from '@/constants/errors';
import type {
  ApplePayPaymentParams,
  AdyenApplePayPaymentParams,
  StripeExpressCheckoutParams,
} from '@/types/paymentHandlers';
import type { AdyenPaymentResponse, StripePaymentResponse } from '@/types/payment';

const handleStripeApplePay = async (
  _checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: StripeExpressCheckoutParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse | null> => {
  const { event, elements } = params;

  const billingDetails = {
    ...event.billingDetails,
    address: {
      ...event.billingDetails?.address,
      line2: event.billingDetails?.address?.line2 || '',
    },
  };

  const applePayResults = await stripeService.createApplePayPaymentMethod(elements, billingDetails);
  if (applePayResults) {
    return (await sendPaymentToServer(
      buildStripeApplePayRequest(applePayResults, providerSettings, event.billingDetails!)
    )) as StripePaymentResponse;
  }
  return null;
};

const handleAdyenApplePay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: AdyenApplePayPaymentParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<AdyenPaymentResponse> => {
  const request = buildAdyenApplePayPaymentRequest(params, providerSettings, checkout.customer);
  return (await sendPaymentToServer(request)) as AdyenPaymentResponse;
};

export const handleApplePayPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params?: ApplePayPaymentParams
): Promise<StripePaymentResponse | AdyenPaymentResponse | null> => {
  if (!params) {
    throw new Error('Apple Pay payment requires parameters');
  }

  const providerSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY
  );

  if (!providerSettings) {
    throw new Error('Apple Pay payment method settings not found');
  }

  try {
    switch (providerSettings.provider) {
      case ProviderEnum.STRIPE:
        return await handleStripeApplePay(
          checkout,
          sendPaymentToServer,
          params as StripeExpressCheckoutParams,
          providerSettings
        );
      case ProviderEnum.ADYEN:
        return await handleAdyenApplePay(
          checkout,
          sendPaymentToServer,
          params as AdyenApplePayPaymentParams,
          providerSettings
        );
      default:
        throw new Error(`Unsupported provider for Apple Pay: ${providerSettings.provider}`);
    }
  } catch (error: unknown) {
    console.error(Errors.apple.errorWhileSendingPayment, { cause: error });
    throw error;
  }
};
